// The «پارسه» price list — the OFFLINE FALLBACK and the first-paint value.
//
// Since 2026-09-12 the price of every AI action comes from ONE server-side
// list: the AI service's active pricing policy, served by the BFF as
// `creditPrices` and read here through `src/lib/useCreditPrices.js`
// (docs/AI_COST_AND_PARSEH_PRICING_MANAGEMENT_DESIGN.md §9.2, phase 3). Every
// price chip and the Credits price list go through `costOf(actionId)` /
// `useCreditPrices()` there, so what is advertised follows the policy the
// service actually debits from.
//
// THIS FILE is what renders before that list has arrived and what renders when
// it never does (offline, BFF down). It mirrors the schedule the AI service
// shipped with (aiassitant/app/domain/services/usage_context.py +
// credits_policy.md):
//   LLM actions 10 پارسه · interaction check 3 پارسه · transcription 5/minute.
// It is NOT where a price is changed — the design doc is the reference, and the
// admin panel's pricing policy is the instrument.
//
// ── WHY THIS LIST DIFFERS FROM THE DOCTOR APP'S ────────────────────────────
// Same prices, different ROWS, because this app performs different actions.
// Every row below is a button a dentist actually presses — nothing here fires
// on load, on a keystroke or on a debounce, which is why «user-triggered» is a
// correctness rule in this app and not a UI preference.
//
// ── 2026-08-25: THE TRANSCRIPTION ROW ARRIVED, AND THIS FILE SAID IT NEVER
//    WOULD ──────────────────────────────────────────────────────────────────
// The removed paragraph gave three reasons for having no transcription row:
// DESIGN.md §7 put voice out of scope, TranscribeAudio was not wrapped in the
// BFF, and advertising a price for something the app cannot do is worse than
// not listing it. The second is simply no longer true — voice dictation goes
// through parsina.bff.dentist's /ws/transcribe now — and the third has
// inverted with it: the dentist IS being charged, so the price has to be on
// the list. §7's actual subject, a continuous visit transcript, is still out
// of scope; what is priced here is FIELD DICTATION.
//
// IT IS THE ONLY METERED ROW. Every other action is a flat charge per press.
// Transcription is per MINUTE and the service rounds UP
// (usage_context.py:36-38, `ceil(elapsed_minutes) * 5`), so the shortest
// possible dictation — two words into شکایت اصلی — still costs a whole 5
// پارسه. That is the service's rule, not this app's, and the UI says so rather
// than letting a dentist infer a per-second price from «۵ / دقیقه».
//
// UNIT LANGUAGE: the unit is «پارسه» / "Parseh" everywhere in the UI, in both
// locales — never «سکه», never "coin", never the Latin "PC". Toman appears only
// at purchase and in the hero's equivalence line. The COIN_* identifiers below
// keep their names on purpose: they are internal, and the wallet's own contract
// still spells the unit "coin".

/** Retail value of one پارسه, in Toman — FALLBACK. The policy's `parsehToman`
 *  wins when known; read it through `coinPriceToman()` in lib/useCreditPrices.js. */
export const COIN_PRICE_TOMAN = 1000;

/**
 * Per-action costs, keyed by the CLIENT action rather than the wallet enum.
 * FALLBACK — read them through `costOf()` in lib/useCreditPrices.js.
 *
 * Each key names a GraphQL operation this app sends, so the mapping from
 * "button the dentist pressed" to "row on the ledger" is checkable by reading
 * one file: Visit.shared.js's three «COSTS PARSINA CREDITS» markers and
 * prescription.js's `checkDrugInteraction` are the whole set.
 */
export const COIN_COSTS = {
  // The ONLY metered cost here: charged per elapsed minute of recording,
  // rounded up. Same number and same rule as the doctor app's
  // TRANSCRIPTION_PER_MIN — one dentist, one wallet, one price.
  TRANSCRIPTION_PER_MIN: 5, // voice dictation — the mic on شکایت اصلی,
  //                           یادداشت and the visit narrative in تشخیص
  DIFFERENTIAL: 10, // visitDifferential — «دریافت پیشنهاد هوش مصنوعی»
  CONFIRM_QUESTIONS: 10, // visitQuestions — پرسش‌های تکمیلی
  ASK_QUESTION: 10, // askVisitQuestion — پرسش از دستیار
  SOAP_REFINE: 10, // refineVisitNote — «تکمیل یادداشت». Charged ONLY when the
  //                  merge produced new content; the server's `charged` flag is
  //                  the signal, never the click.
  TOOTH_FINDINGS: 10, // extractToothFindings — یافته‌های دندان از متن. Bills
  //                     separately from the two reads above so a chart the
  //                     assistant could not read does not cost the note draft.
  VISIT_ADVICE: 10, // generateVisitAdvice — نکات بالینی دستیار
  OCR: 10, // the OCR half of uploadVisitFile — reading an uploaded document
  INTERACTION_CHECK: 3, // checkDrugInteraction — بررسی تداخل دارویی
};

// The «هر عملیات چند پارسه؟» card. `perKey` marks a metered action — the
// transcription row is the only one, and it leads the list for that reason
// rather than by price: a row priced differently from every other row is the
// one a dentist must not skim past. The rest run by price descending, then by
// how often a dentist meets the action, so "what does X cost?" is one glance.
//
// Same ordering rule as the doctor app's list, which also leads with
// transcription at 5/minute above its flat 10s.
// `action` names the COIN_COSTS key each row prices, so `resolvedPriceList()`
// in lib/useCreditPrices.js can swap `cost` for the policy's figure row by row.
export const PRICE_LIST = [
  { id: 'transcription', action: 'TRANSCRIPTION_PER_MIN', labelKey: 'credits.price.transcription', cost: COIN_COSTS.TRANSCRIPTION_PER_MIN, perKey: 'common.units.minute' },
  { id: 'differential', action: 'DIFFERENTIAL', labelKey: 'credits.price.differential', cost: COIN_COSTS.DIFFERENTIAL },
  { id: 'advice', action: 'VISIT_ADVICE', labelKey: 'credits.price.advice', cost: COIN_COSTS.VISIT_ADVICE },
  { id: 'soap', action: 'SOAP_REFINE', labelKey: 'credits.price.soap', cost: COIN_COSTS.SOAP_REFINE },
  { id: 'teeth', action: 'TOOTH_FINDINGS', labelKey: 'credits.price.teeth', cost: COIN_COSTS.TOOTH_FINDINGS },
  { id: 'confirmq', action: 'CONFIRM_QUESTIONS', labelKey: 'credits.price.confirmq', cost: COIN_COSTS.CONFIRM_QUESTIONS },
  { id: 'ask', action: 'ASK_QUESTION', labelKey: 'credits.price.ask', cost: COIN_COSTS.ASK_QUESTION },
  { id: 'ocr', action: 'OCR', labelKey: 'credits.price.ocr', cost: COIN_COSTS.OCR },
  { id: 'interaction', action: 'INTERACTION_CHECK', labelKey: 'credits.price.interaction', cost: COIN_COSTS.INTERACTION_CHECK },
];

/**
 * پارسه as a COUNT, never money-formatted — a پارسه is not a currency.
 *
 * `formatNumber` comes from `useTranslation()` and is optional: called without
 * it (a test, a plain module) this still renders, just in Latin digits. That
 * keeps the function pure and callable outside React.
 */
export function fmtCoins(n, formatNumber) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const rounded = Math.round(Number(n));
  return formatNumber ? formatNumber(rounded) : String(rounded);
}

/**
 * «≈ N تومان» for a پارسه amount — the hero and the top-up sheet only.
 *
 * NOT a money formatter: those convert rials to toman, and COIN_PRICE_TOMAN is
 * already a toman figure. Passing this through one would divide the price by
 * ten.
 */
export function coinsToTomanLabel(coins, { formatNumber, tomanLabel, tomanPerCoin = COIN_PRICE_TOMAN } = {}) {
  if (coins == null || !Number.isFinite(Number(coins))) return '';
  const toman = Math.round(Number(coins)) * tomanPerCoin;
  const text = formatNumber ? formatNumber(toman) : String(toman);
  return `≈ ${text} ${tomanLabel || ''}`.trim();
}
